import { Box, Button, Flex, Heading, VStack, Tooltip, useToast, Text } from '@chakra-ui/react'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'

import { IBannerData, IBannerRequest } from '../../../interfaces/Website/Banners/IBanners'
import BannerService from '../../../services/Website/BannerService'
import { BannerPageDeleteModal } from './BannerPageDeleteModal'
import BannerPageSortableBanner from './BannerPageSortableBanner'

function BannerPage() {
  const cancelRef = useRef(null)
  const service = new BannerService()
  const toast = useToast()
  const [banners, setBanners] = useState<IBannerData[]>([])
  const [errors, setErrors] = useState<Record<keyof IBannerRequest, string[]>[]>([])
  const [deleteIndex, setDeleteIndex] = useState<number | null>()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over) return

    if (active.id !== over.id) {
      setBanners((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id)
        const newIndex = items.findIndex((item) => item.id.toString() === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const fetchBanners = async () => {
    try {
      const response = await service.list()

      const sortedBanners = response.data.sort((a: IBannerData, b: IBannerData) => {
        const aActive = !!a.is_active
        const bActive = !!b.is_active

        if (aActive === bActive) return 0
        return aActive ? -1 : 1
      })

      setBanners(sortedBanners)
    } catch (error) {
      toast({
        title: 'Erro ao buscar banners',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  function addBanner() {
    const newBanner: IBannerData = {
      id: 0, // id temporário para banner novo
      title: '',
      subtitle: '',
      text: '',
      image: null,
      video_url: '',
      cta_text: '',
      cta_link: '',
      is_active: true
    }

    setBanners([...banners, newBanner])
  }

  function repositionBanner(fromIndex: number, toIndex: number) {
    const newArray = [...banners]

    if (fromIndex < 0 || fromIndex >= newArray.length || toIndex < 0 || toIndex >= newArray.length) {
      return
    }

    const [movedItem] = newArray.splice(fromIndex, 1)
    newArray.splice(toIndex, 0, movedItem)
    setBanners(newArray)
  }

  const handleDelete = async () => {
    try {
      if (deleteIndex == null) return
      if (banners[deleteIndex].id) {
        await service.delete(banners[deleteIndex].id)
      }
      const newBanners = [...banners]
      newBanners.splice(deleteIndex, 1)
      setBanners(newBanners)

      toast({
        title: 'Banner excluído',
        description: 'Banner excluído com sucesso.',
        status: 'success',
        duration: 5000,
        isClosable: true
      })

      setDeleteIndex(null)
    } catch (error) {
      toast({
        title: 'Erro ao excluir banner',
        description: 'Erro ao excluir banner, tente novamente mais tarde.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleSwitch = async (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    try {
      const shouldEnableBanner = e.target.checked

      if (shouldEnableBanner) {
        await service.enable(id)
      } else {
        await service.disable(id)
      }

      setBanners((prevBanners) =>
        prevBanners.map((banner) => (banner.id === id ? { ...banner, is_active: shouldEnableBanner } : banner))
      )

      toast({
        title: `Banner ${shouldEnableBanner ? 'ativado' : 'desativado'} com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (err) {
      toast({
        title: 'Erro ao atualizar banner',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  useEffect(() => {
    fetchBanners()
  }, [])

  const openDeleteDialog = (id: number) => {
    setDeleteIndex(id)
  }

  const handleSubmit = async () => {
    const updatedBanners = [...banners]

    await Promise.all(
      updatedBanners.map(async ({ id, ...banner }, key) => {
        const bannerToSend: IBannerRequest & { is_active: number; image?: File } = {
          ...banner,
          is_active: banner.is_active ? 1 : 0,
          image: banner.image instanceof File ? banner.image : undefined
        }

        if (id) {
          if (typeof banner.image === 'string') {
            bannerToSend.image = undefined
          }

          try {
            await service.update(id, bannerToSend)
            setErrors((_errors) => {
              _errors[key] = {} as Record<keyof IBannerRequest, string[]>
              return _errors
            })
          } catch (err) {
            const error = err as AxiosError<{ errors: Record<keyof IBannerRequest, string[]> }>
            if (error.response?.status === 422) {
              setErrors((_errors) => {
                _errors[key] = error.response!.data.errors
                return _errors
              })
            } else {
              toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao atualizar o banner.',
                status: 'error',
                duration: 5000,
                isClosable: true
              })
            }
          }
        } else {
          try {
            const { data } = await service.create(bannerToSend)
            updatedBanners[key] = data
          } catch (err) {
            const error = err as AxiosError<{ errors: Record<keyof IBannerRequest, string[]> }>
            if (error.response?.status === 422) {
              setErrors((_errors) => {
                _errors[key] = error.response!.data.errors
                return _errors
              })
            } else {
              toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao criar o banner.',
                status: 'error',
                duration: 5000,
                isClosable: true,
              })
            }
          }
        }
      })
    )

    setBanners(updatedBanners)
    await service.reorder(updatedBanners.map(({ id }) => id))
    await fetchBanners()
  }

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Gerenciar Banners</Heading>
      </Flex>

      {!banners.length && <Text>Não há banners cadastrados</Text>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={banners.map((banner) => banner.id.toString())} strategy={verticalListSortingStrategy}>
          <VStack spacing={10} align="stretch" width="100%">
            {banners.map((banner, i) => (
              <BannerPageSortableBanner
                key={banner.id}
                id={banner.id.toString()}
                data={banner}
                onChange={(data) => {
                  setBanners((_banners) => {
                    const newBanners = [..._banners]
                    newBanners[i] = data
                    return newBanners
                  })
                }}
                errors={errors[i]}
                changePosition={(direction) => repositionBanner(i, i + (direction === 'up' ? -1 : 1))}
                openDeleteDialog={() => openDeleteDialog(i)}
                handleSwitch={handleSwitch}
              />
            ))}
          </VStack>
        </SortableContext>
      </DndContext>

      <BannerPageDeleteModal
        isOpen={deleteIndex != null}
        onClose={() => setDeleteIndex(null)}
        leastDestructiveRef={cancelRef}
        handleDelete={handleDelete}
      />
      {/* add button */}
      <Tooltip label="Adicionar " aria-label="Adicionar " placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={addBanner}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      {/* save button */}
      <Tooltip label="Salvar " aria-label="Salvar">
        <Button
          colorScheme="green"
          position="fixed"
          bottom="100px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={handleSubmit}
        >
          <i className="lni lni-checkmark"></i>
        </Button>
      </Tooltip>
    </Box>
  )
}

export default BannerPage
